import * as React from 'react';
import { cn } from '@/utils/cn';
import { IconInfoCircle } from '@tabler/icons-react';

export interface TeeTextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  hint?: string;
  destructive?: boolean;
  showLabel?: boolean;
  showHint?: boolean;
  required?: boolean;
  subLabel?: string;
}

const TeeTextArea = React.forwardRef<HTMLTextAreaElement, TeeTextAreaProps>(
  (
    {
      className,
      label,
      hint,
      destructive = false,
      showLabel = true,
      showHint = true,
      required = false,
      subLabel,
      disabled,
      ...props
    },
    ref
  ) => {
    const labelClasses = cn(
      'text-body-s font-medium text-[var(--text-label-title)]',
      destructive && 'text-red-500'
    );

    const hintClasses = cn(
      'text-body-xs font-medium text-[var(--text-content-sub-title)]',
      destructive && 'text-red-500'
    );

    const textareaClasses = cn(
      'flex-1 rounded-[var(--spacing-xs)] border border-solid',
      'bg-[var(--background-card)] px-3 py-2',
      'font-[family-name:var(--type-font-family-body,\'Urbanist:regular\',sans-serif)]',
      'text-[length:var(--font-size-body-s,14px)]',
      'leading-[var(--line-height-body-s,22px)]',
      'text-[var(--text-content-title)] placeholder:text-[var(--text-content-sub-title)]',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
      'transition-all duration-200 resize-none',
      destructive
        ? 'border-red-500 focus-visible:ring-red-500'
        : 'border-[var(--border-default)] focus-visible:ring-[hsl(var(--ring))]',
      disabled && 'bg-[var(--background-input-field-disabled)] border-[var(--border-default)] opacity-50 cursor-not-allowed'
    );

    return (
      <div className="flex w-full flex-col gap-1.5">
        {showLabel && label && (
          <div className="flex items-center gap-1">
            <label className={labelClasses}>{label}</label>
            {subLabel && (
              <span className="text-body-s font-normal text-[var(--text-label-sub-label)]">
                {subLabel}
              </span>
            )}
            {required && <span className={labelClasses}>*</span>}
          </div>
        )}

        <textarea
          ref={ref}
          disabled={disabled}
          className={cn(textareaClasses, className)}
          {...props}
        />

        {showHint && hint && (
          <div className="flex items-start gap-1">
            <IconInfoCircle
              size={16}
              className={cn(
                'mt-0.5 flex-shrink-0',
                destructive ? 'text-red-500' : 'text-[var(--text-content-sub-title)]'
              )}
            />
            <p className={hintClasses}>{hint}</p>
          </div>
        )}
      </div>
    );
  }
);

TeeTextArea.displayName = 'TeeTextArea';

export { TeeTextArea };
export type { TeeTextAreaProps as TeeTextAreaPropsType };
